import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import { inputStyle } from '@/lib/format';

import { educationLabel, plainLabel, type EducationCatalog, type EducationKind, type EducationMenuItem } from './safeJournalApi';

/**
 * 안전교육 종류 고르개 (DOC-122 §16 · 사용자 지시 2026-10-23 「종류를 검색이나 드롭박스로 골라서 들어가면 정해진 양식대로」).
 *
 * <p>목록은 서버 카탈로그의 {@code menu} 차례다 — 정기 · 관리감독자 · 위기상황 · 성희롱 · 장애인 · 채용 시 · 특수형태 ·
 * MSDS · 특별안전보건교육(39종) · 작업내용 변경 시 · 기타. 특별을 누르면 39종이 펼쳐진다. 검색은 종류 이름과
 * 39종의 짧은 이름 · 법령 이름을 함께 찾는다(「비계」 → 특별 · 26. 비계작업).
 *
 * <p>{@code compact} 는 고른 이름 한 칸이고 누르면 같은 목록이 그 아래로 펼쳐진다(왼쪽 패널에서 바꿀 때).
 */
export function EducationPicker({
  catalog,
  kind,
  sub,
  onPick,
  compact,
}: {
  catalog: EducationCatalog | undefined;
  kind: string | null;
  sub: string | null;
  onPick: (kind: EducationKind, sub: string | null) => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(!compact);
  const [query, setQuery] = useState('');
  const [special, setSpecial] = useState(false);
  const needsWork = kind === 'SPECIAL' && !sub;
  useEffect(() => {
    if (!needsWork) {
      // 작업을 다른 곳(소분류 고르기 · 종이)에서 골랐으면 펼친 목록을 접는다.
      if (compact) setOpen(false);
      setSpecial(false);
      return;
    }
    setOpen(true);
    setSpecial(true);
  }, [needsWork, compact]);
  const box = useRef<HTMLDivElement>(null);

  // 바깥을 누르면 접는다(compact 만).
  useEffect(() => {
    if (!compact || !open) return;
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [compact, open]);

  // 서버는 null 칸을 빼고 보낸다(jackson non_null) — 「특별안전보건교육」 의 sub 가 null 이 아니라 없음(undefined)으로 온다.
  // 그래서 sub 는 늘 `?? null` 로 읽는다. 안 그러면 특별을 눌렀을 때 39종이 펼쳐지지 않고 바로 골라진다(2026-11-04).
  const menu: EducationMenuItem[] = useMemo(
    () => catalog?.menu ?? FALLBACK_MENU,
    [catalog],
  );
  const specials = catalog?.kinds.find((one) => one.code === 'SPECIAL')?.subs ?? [];
  const q = query.trim().replace(/\s+/g, '');
  const hit = (text: string | null | undefined) => !!text && text.replace(/\s+/g, '').includes(q);
  const menuShown = q ? menu.filter((one) => hit(one.label)) : menu;
  const specialShown = q ? specials.filter((one) => hit(one.short) || hit(one.label)) : special ? specials : [];

  const current = (() => {
    if (!kind) return '';
    const item = menu.find((one) => one.kind === kind && (one.sub ?? null) === sub);
    if (item) return plainLabel(item.label);
    if (kind === 'SPECIAL') {
      const work = specials.find((one) => one.code === sub);
      return work ? `특별안전보건교육 · ${plainLabel(work.short ?? work.label)}` : '특별안전보건교육';
    }
    return educationLabel(catalog, kind, sub);
  })();

  const pick = (k: EducationKind, s: string | null) => {
    onPick(k, s);
    setQuery('');
    setSpecial(false);
    if (compact) setOpen(false);
  };

  const panel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '10px' : '14px' }}>
      {!compact && <style>{CARD_STYLES}</style>}
      <input
        className="input"
        style={compact ? inputStyle : { ...inputStyle, height: '44px', fontSize: '15px', padding: '0 14px' }}
        placeholder="교육 종류 · 작업 이름 검색"
        value={query}
        autoFocus={!compact || open}
        onChange={(e) => setQuery(e.target.value)}
      />
      {menuShown.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: compact ? '6px' : '10px' }}>
          {menuShown.map((one) => {
            const on = kind === one.kind && ((one.sub ?? null) === null ? true : (one.sub ?? null) === sub);
            const toggle = (one.sub ?? null) === null && one.kind === 'SPECIAL';
            const label = (
              <>
                {plainLabel(one.label)}
                {toggle ? (special ? ' ▴' : ' ▾') : ''}
              </>
            );
            if (compact) {
              return (
                <button key={one.code} type="button" style={on ? { ...item, ...itemOn } : item} onClick={() => (toggle ? setSpecial((v) => !v) : pick(one.kind, one.sub ?? null))}>
                  {label}
                </button>
              );
            }
            return (
              <button
                key={one.code}
                type="button"
                className={`edu-card ${on || (toggle && special) ? 'edu-card-on' : ''}`}
                onClick={() => (toggle ? setSpecial((v) => !v) : pick(one.kind, one.sub ?? null))}
              >
                <span className="edu-card-name">{label}</span>
              </button>
            );
          })}
        </div>
      )}
      {specialShown.length > 0 && (
        <div
          style={
            compact
              ? { display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '360px', overflowY: 'auto' }
              : { display: 'flex', flexDirection: 'column', maxHeight: '360px', overflowY: 'auto', border: '1px solid var(--border-strong)', borderRadius: '10px' }
          }
        >
          {specialShown.map((one) => (
            <button
              key={one.code}
              type="button"
              className={compact ? undefined : `edu-row ${kind === 'SPECIAL' && sub === one.code ? 'edu-card-on' : ''}`}
              style={compact ? (kind === 'SPECIAL' && sub === one.code ? { ...row, ...itemOn } : row) : undefined}
              onClick={() => pick('SPECIAL', one.code)}
            >
              <b>{plainLabel(one.short ?? one.label)}</b>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (!compact) return panel;
  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button type="button" className="input" style={{ ...inputStyle, minHeight: '38px', textAlign: 'left', width: '100%' }} onClick={() => setOpen((v) => !v)}>
        {current || '교육 종류 선택'} ▾
      </button>
      {/* 왼쪽 패널 안에서 펼친다 — 띄우면 패널의 스크롤 칸에 잘린다. */}
      {open && (
        <div style={{ marginTop: '6px', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px', background: 'var(--bg-card, #fff)' }}>
          {panel}
        </div>
      )}
    </div>
  );
}

/** 큰 고르개(새 일지의 첫 화면) 단추. 마우스를 올리면 바탕이 바뀐다 — 인라인 스타일로는 :hover 를 못 쓴다. */
const CARD_STYLES = `
.edu-card { display: flex; flex-direction: column; justify-content: center; gap: 4px; min-height: 56px; padding: 12px 14px;
  border: 1px solid var(--border-strong); border-radius: 10px; background: var(--bg-card, #fff); text-align: left; cursor: pointer;
  transition: border-color 0.12s, background 0.12s, box-shadow 0.12s; }
.edu-card:hover { border-color: var(--accent-light); background: var(--accent-bg); }
.edu-card:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3); }
.edu-card-on { border-color: var(--accent-light); background: var(--accent-bg); }
.edu-row { flex-shrink: 0; padding: 10px 14px; border: 0; border-bottom: 1px solid var(--border); background: var(--bg-card, #fff); text-align: left;
  font-size: 13px; line-height: 1.5; cursor: pointer; word-break: keep-all; }
.edu-row:last-child { border-bottom: 0; }
.edu-row:hover { background: var(--accent-bg); }
.edu-card-name { font-size: 14px; font-weight: 700; color: var(--text-primary); word-break: keep-all; }
`;

const item: CSSProperties = {
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  background: 'var(--bg-card, #fff)',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};
const itemOn: CSSProperties = { borderColor: '#2563eb', background: 'rgba(37, 99, 235, 0.08)' };
const row: CSSProperties = {
  flexShrink: 0,
  padding: '7px 10px',
  border: '1px solid transparent',
  borderRadius: '6px',
  background: 'transparent',
  textAlign: 'left',
  fontSize: '13px',
  lineHeight: 1.45,
  cursor: 'pointer',
  whiteSpace: 'normal',
  wordBreak: 'keep-all',
};

/**
 * 서버 카탈로그에 고르개 차례(menu)가 없을 때(서버가 V537 앞 판) 쓰는 같은 차례 — catalog.json 의 menu 와 한 벌.
 */
const FALLBACK_MENU: EducationMenuItem[] = [
  { code: 'REGULAR', label: '정기교육', kind: 'WORKER', sub: 'REGULAR' },
  { code: 'SUPERVISOR', label: '관리감독자 교육', kind: 'SUPERVISOR', sub: 'REGULAR' },
  { code: 'CRISIS', label: '위기상황대응교육', kind: 'CRISIS', sub: 'CRISIS' },
  { code: 'HARASSMENT', label: '성희롱예방교육', kind: 'HARASSMENT', sub: 'HARASSMENT' },
  { code: 'DISABILITY', label: '장애인인식개선교육', kind: 'DISABILITY', sub: 'DISABILITY' },
  { code: 'HIRE', label: '채용 시 교육', kind: 'WORKER', sub: 'HIRE' },
  { code: 'SPECIAL_FORM', label: '특수형태근로종사자 교육', kind: 'SPECIAL_FORM', sub: 'FIRST' },
  { code: 'MSDS', label: 'MSDS 교육', kind: 'MSDS', sub: 'MSDS' },
  { code: 'SPECIAL', label: '특별안전보건교육 (39종)', kind: 'SPECIAL', sub: null },
  { code: 'CHANGE', label: '작업내용 변경 시 교육', kind: 'WORKER', sub: 'CHANGE' },
  { code: 'OTHER', label: '기타', kind: 'OTHER', sub: null },
];
